import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { errorMessage } from '../lib/api-error';
import { useAuth } from '../lib/auth-context';
import { isReadOnly } from '../lib/permissions';
import {
  CUSTOMER_TYPE_LABELS,
  countryLabel,
  useContacts,
  type Contact,
  type ListContactsParams,
} from '../lib/contacts-api';
import { NewContactModal } from './ContactDetail';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  Select,
  Spinner,
  Table,
  Td,
  Th,
} from '../components/ui';

/**
 * Cari listesi — müşteri ve tedarikçi TEK tabloda.
 *
 * Liste bir arama ekranıdır ama gösterdiği sütunlar keyfi değil: `KDV
 * numarası`, `ülke` ve `müşteri tipi` KDV çözümleyicisinin girdisidir ve
 * eksik olanları burada görmek, faturayı kesmeye çalışırken görmekten
 * ucuzdur. VIES rozeti de bu yüzden numaranın yanında durur.
 */

const PAGE_SIZE = 25;

type RoleFilter = 'all' | 'customer' | 'supplier';
type StatusFilter = 'all' | 'active' | 'passive';

export function ContactsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const readOnly = isReadOnly(user);

  const [term, setTerm] = useState('');
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<RoleFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [offset, setOffset] = useState(0);
  const [creating, setCreating] = useState(false);

  /*
   * Arama kutusu geciktirilir: her tuş vuruşunda bir istek atmak, `q`
   * parametresinin ILIKE + kanonik numara karşılaştırması yapan sorgusunu
   * gereksizce çalıştırır ve sonuçlar yazma hızına göre sırasız döner.
   */
  useEffect(() => {
    const timer = setTimeout(() => setSearch(term.trim()), 300);
    return () => clearTimeout(timer);
  }, [term]);

  /*
   * Süzgeç değişince İLK SAYFAYA dönülür. Aksi hâlde 3. sayfadayken süzgeci
   * daraltmak "sonuç yok" gösterir — oysa sonuç vardır, sadece 3. sayfada
   * değildir; operatör süzgecin bozuk olduğunu sanır.
   */
  useEffect(() => {
    setOffset(0);
  }, [search, role, status]);

  const params: ListContactsParams = {
    limit: PAGE_SIZE,
    offset,
    ...(search ? { q: search } : {}),
    ...(role === 'customer' ? { isCustomer: true } : {}),
    ...(role === 'supplier' ? { isSupplier: true } : {}),
    ...(status === 'all' ? {} : { active: status === 'active' }),
  };

  const list = useContacts(params);
  const data = list.data ?? null;

  const total = data?.total ?? 0;
  const shownFrom = total === 0 ? 0 : offset + 1;
  const shownTo = data ? offset + data.items.length : 0;
  const hasPrev = offset > 0;
  const hasNext = data !== null && offset + data.items.length < total;

  return (
    <>
      <PageHeader
        title="Cariler"
        description="Müşteri ve tedarikçi kayıtları. Müşteri tipi, ülke ve KDV numarası KDV çözümleyicisinin girdisidir."
        actions={!readOnly && <Button onClick={() => setCreating(true)}>Yeni cari</Button>}
      />

      <Card className="mb-4">
        <div className="grid gap-3 px-4 py-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <Input
              label="Ara"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Ad, unvan, KDV/işletme numarası, e-posta, şehir"
              hint="Numarayı ayraçlı yapıştırabilirsiniz: sunucu 'BE 1041.328.256' ile 'BE1041328256' aramasını birlikte yapar."
            />
          </div>

          <Select
            label="Rol"
            value={role}
            onChange={(e) => setRole(e.target.value as RoleFilter)}
            hint="Bir cari hem müşteri hem tedarikçi olabilir."
          >
            <option value="all">Tümü</option>
            <option value="customer">Müşteriler</option>
            <option value="supplier">Tedarikçiler</option>
          </Select>

          <Select
            label="Durum"
            value={status}
            onChange={(e) => setStatus(e.target.value as StatusFilter)}
            hint="Belgesi olan cari silinmez, pasifleştirilir — pasifler bu süzgeçle bulunur."
          >
            <option value="all">Tümü</option>
            <option value="active">Aktif</option>
            <option value="passive">Pasif</option>
          </Select>
        </div>
      </Card>

      <Card>
        {list.isPending ? (
          <div className="px-4 py-10 text-center">
            <Spinner size={20} />
          </div>
        ) : list.isError ? (
          <ErrorState message={errorMessage(list.error)} />
        ) : data === null || data.items.length === 0 ? (
          <EmptyState
            title="Cari bulunamadı"
            description={
              search || role !== 'all' || status !== 'all'
                ? 'Süzgeçleri gevşetip tekrar deneyin.'
                : 'Henüz cari kaydı yok. "Yeni cari" ile başlayın.'
            }
          />
        ) : (
          <>
            {/* Süzgeç değişirken eski sonuç soluk kalır; tablo boşalıp zıplamaz. */}
            <div className={list.isFetching ? 'opacity-60 transition-opacity' : undefined}>
              <Table>
                <thead>
                  <tr>
                    <Th>Ad</Th>
                    <Th>KDV numarası</Th>
                    <Th>Ülke</Th>
                    <Th>Müşteri tipi</Th>
                    <Th>Durum</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((contact) => (
                    <tr
                      key={contact.id}
                      className="cursor-pointer hover:bg-[var(--c-surface-alt)]"
                      onClick={() => void navigate(`/contacts/${contact.id}`)}
                    >
                      <Td>
                        <Link
                          to={`/contacts/${contact.id}`}
                          className="font-medium text-[var(--c-brand)] hover:underline"
                          // Satırın kendi tıklaması zaten yönlendiriyor; bağlantı
                          // ikinci kez tetiklemesin.
                          onClick={(e) => e.stopPropagation()}
                        >
                          {contact.name}
                        </Link>
                        {contact.legalName && (
                          <span className="block text-xs text-[var(--c-ink-faint)]">
                            {contact.legalName}
                          </span>
                        )}
                      </Td>
                      <Td>
                        {contact.vatNumber ? (
                          <span className="font-mono text-[13px]">{contact.vatNumber}</span>
                        ) : (
                          <span className="text-[var(--c-ink-faint)]">—</span>
                        )}
                        <ViesBadge contact={contact} />
                      </Td>
                      <Td>{countryLabel(contact.countryCode)}</Td>
                      <Td>
                        {contact.customerType ? (
                          CUSTOMER_TYPE_LABELS[contact.customerType]
                        ) : (
                          <span
                            className="text-[var(--c-warn)]"
                            title="Çözümleyici B2B/B2C ayrımını yapamaz; bu cariye fatura düzenlenemez."
                          >
                            seçilmedi
                          </span>
                        )}
                      </Td>
                      <Td>
                        <div className="flex flex-wrap gap-1">
                          <Badge tone={contact.active ? 'ok' : 'neutral'}>
                            {contact.active ? 'aktif' : 'pasif'}
                          </Badge>
                          {contact.isCustomer && <Badge tone="brand">müşteri</Badge>}
                          {contact.isSupplier && <Badge tone="brand">tedarikçi</Badge>}
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--c-border)] px-4 py-3">
              <span className="text-xs text-[var(--c-ink-soft)]">
                {shownFrom}–{shownTo} / {total} cari
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!hasPrev}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  Önceki
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!hasNext}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  Sonraki
                </Button>
              </div>
            </footer>
          </>
        )}
      </Card>

      {creating && (
        <NewContactModal
          onClose={() => setCreating(false)}
          onCreated={(contact) => {
            setCreating(false);
            // Kayıt açıldı; kalan alanlar (adres, banka, gelişmiş) detayda.
            void navigate(`/contacts/${contact.id}`);
          }}
        />
      )}
    </>
  );
}

/**
 * Listedeki VIES rozeti.
 *
 * ⚠ Üç durum ayrı: doğrulandı / geçersiz bulundu / SORULAMADI. Sonuncusu
 * kırmızı DEĞİLDİR — üye devlet servisleri düzenli olarak kapanır ve
 * "ulaşılamadı"yı olumsuz göstermek meşru bir müşteriyi karalar.
 */
function ViesBadge({ contact }: { contact: Contact }) {
  const check = contact.viesCheck;
  if (!check) return null;

  const [tone, text, title] =
    check.valid === true
      ? (['ok', 'VIES ✓', 'Son sorguda numara geçerliydi.'] as const)
      : check.valid === false
        ? (['danger', 'VIES ✗', 'Son sorguda VIES bu numarayı geçerli bulmadı.'] as const)
        : ([
            'warn',
            'VIES ?',
            'Son sorguda VIES’e ulaşılamadı — bilgi yok demektir, "geçersiz" demek değildir.',
          ] as const);

  return (
    <span className="ml-2 inline-block align-middle" title={title}>
      <Badge tone={tone}>{text}</Badge>
    </span>
  );
}
